import React, { useState, useEffect } from 'react';
import { useAuth, ProtectedRoute } from '../App';
import { 
  LayoutDashboard, Home, Calendar, Image, Trophy, Award, Gift, 
  Radio, Video, Phone, MessageSquare, Settings, LogOut, Users,
  BookOpen, Plus, Edit2, Trash2, Save, X, CheckCircle, Clock,
  TrendingUp, Eye, Copy, Flag, AlertCircle, Upload, Mail
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin Layout with Sidebar
function AdminLayout({ children, activeSection }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleBackToSite = () => {
    navigate('/');
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { id: 'stream', label: 'Live Stream', icon: Radio, path: '/admin/stream' },
    { id: 'schedule', label: 'Schedule', icon: Calendar, path: '/admin/schedule' },
    { id: 'panel', label: 'Panel Members', icon: Users, path: '/admin/panel' },
    { id: 'lessons', label: 'Lessons', icon: BookOpen, path: '/admin/lessons' },
    { id: 'archives', label: 'Archives', icon: Video, path: '/admin/archives' },
    { id: 'forum', label: 'Forum', icon: MessageSquare, path: '/admin/forum' },
    { id: 'contacts', label: 'Contact Us', icon: Mail, path: '/admin/contacts' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen bg-black">
      {/* Sidebar */}
      <aside className="w-64 bg-black border-r border-zinc-800 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Panel</h1>
              <p className="text-xs text-zinc-500">Luck or Skills</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-yellow-500/10 text-yellow-500 font-semibold' 
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-zinc-800 space-y-2">
          <button
            onClick={handleBackToSite}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition-all w-full"
            data-testid="admin-back-to-site"
          >
            <Home className="w-5 h-5" />
            <span>Back to Site</span>
          </button>
          <div className="text-xs text-zinc-500 px-4">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition-all w-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}

// Dashboard Overview
function DashboardOverview() {
  const [stats, setStats] = useState({
    panelMembers: 0,
    lessons: 0,
    archives: 0,
    scheduleItems: 0,
    forumThreads: 0,
    forumReplies: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [panel, lessons, archives, schedule, threads] = await Promise.all([
          fetch(`${BACKEND_URL}/api/panel`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
          fetch(`${BACKEND_URL}/api/lessons`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
          fetch(`${BACKEND_URL}/api/archives`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
          fetch(`${BACKEND_URL}/api/schedule`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
          fetch(`${BACKEND_URL}/api/forum/threads`, { credentials: 'include' }).then(r => r.json()).catch(() => [])
        ]);

        // Ensure threads is an array before using reduce
        const threadsArray = Array.isArray(threads) ? threads : [];

        setStats({
          panelMembers: panel.length || 0,
          lessons: lessons.length || 0,
          archives: archives.length || 0,
          scheduleItems: schedule.length || 0,
          forumThreads: threadsArray.length || 0,
          forumReplies: threadsArray.reduce((acc, t) => acc + (t.reply_count || 0), 0)
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { 
      label: 'Panel Members', 
      value: stats.panelMembers, 
      icon: Users, 
      color: 'blue',
      link: '/admin/panel'
    },
    { 
      label: 'Scheduled Streams', 
      value: stats.scheduleItems, 
      icon: Calendar, 
      color: 'green',
      link: '/admin/schedule'
    },
    { 
      label: 'Lessons', 
      value: stats.lessons, 
      icon: BookOpen, 
      color: 'yellow',
      link: '/admin/lessons'
    },
    { 
      label: 'Stream Archives', 
      value: stats.archives, 
      icon: Video, 
      color: 'purple',
      link: '/admin/archives'
    },
    { 
      label: 'Forum Threads', 
      value: stats.forumThreads, 
      icon: MessageSquare, 
      color: 'pink',
      link: '/admin/forum'
    },
    { 
      label: 'Forum Replies', 
      value: stats.forumReplies, 
      icon: Flag, 
      color: 'red',
      link: '/admin/forum'
    }
  ];

  const colorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    purple: 'text-purple-500',
    pink: 'text-pink-500',
    red: 'text-red-500'
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-zinc-400">Welcome back! Here's an overview of your content.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Link
              key={idx}
              to={stat.link}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-all cursor-pointer group"
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-zinc-400 mb-1">{stat.label}</p>
                  <p className="text-4xl font-bold text-white">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-${stat.color}-500/10 ${colorClasses[stat.color]} group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Tips */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Quick Tips</h2>
        <ul className="space-y-4 text-zinc-300">
          <li className="flex items-start gap-3">
            <span className="text-yellow-500">•</span>
            <span>Use the <span className="text-yellow-500 font-semibold">Schedule</span> section to plan your weekly livestream sessions.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-yellow-500">•</span>
            <span>Upload archived recordings to the <span className="text-yellow-500 font-semibold">Archives</span> for members to review.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-yellow-500">•</span>
            <span>Keep the <span className="text-yellow-500 font-semibold">Panel Members</span> section updated with current instructor bios.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-yellow-500">•</span>
            <span>Check <span className="text-yellow-500 font-semibold">Forum</span> regularly to engage with community questions.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// Panel Members Management Section
function PanelManagement() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', bio: '', picture_url: '', poker_background: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/panel`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${BACKEND_URL}/api/admin/panel/${editing}` 
        : `${BACKEND_URL}/api/admin/panel`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        await fetchMembers();
        setEditing(null);
        setFormData({ name: '', bio: '', picture_url: '', poker_background: '' });
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Error saving member:', error);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this panel member?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/panel/${memberId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchMembers();
      }
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const handleEdit = (member) => {
    setEditing(member.member_id);
    setFormData({
      name: member.name,
      bio: member.bio,
      picture_url: member.picture_url,
      poker_background: member.poker_background || ''
    });
    setSelectedFile(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingImage(true);
    setSelectedFile(file);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const res = await fetch(`${BACKEND_URL}/api/admin/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData
      });

      if (res.ok) {
        const data = await res.json();
        // Update the picture_url with the uploaded image URL
        setFormData({ ...formData, picture_url: `${BACKEND_URL}${data.image_url}` });
        alert('Image uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      setSelectedFile(null);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Panel Members</h1>
          <p className="text-zinc-400">Manage your instructor team</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormData({ name: '', bio: '', picture_url: '', poker_background: '' });
            setSelectedFile(null);
          }}
          className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Member
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editing ? 'Edit Member' : 'Add New Member'}
          </h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Poker Background</label>
              <input
                type="text"
                value={formData.poker_background}
                onChange={(e) => setFormData({ ...formData, poker_background: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="20+ years experience"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[100px]"
                required
              />
            </div>
            
            {/* Picture Upload Section */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-zinc-300">Profile Picture</label>
              
              {/* File Upload Option */}
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 hover:border-yellow-500/50 transition-colors">
                <div className="flex flex-col items-center justify-center gap-3">
                  <Upload className="w-8 h-8 text-zinc-500" />
                  <div className="text-center">
                    <p className="text-sm text-zinc-400 mb-2">
                      {selectedFile ? `Selected: ${selectedFile.name}` : 'Upload from your computer'}
                    </p>
                    <label className="cursor-pointer">
                      <span className="text-yellow-500 hover:text-yellow-400 text-sm font-medium">
                        Choose File
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  {uploadingImage && (
                    <p className="text-sm text-yellow-500 animate-pulse">Uploading...</p>
                  )}
                </div>
              </div>
              
              {/* OR Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-zinc-800"></div>
                <span className="text-xs text-zinc-500 uppercase">Or</span>
                <div className="flex-1 h-px bg-zinc-800"></div>
              </div>
              
              {/* URL Input Option */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Paste Image URL</label>
                <input
                  type="url"
                  value={formData.picture_url}
                  onChange={(e) => setFormData({ ...formData, picture_url: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              
              {/* Image Preview */}
              {formData.picture_url && (
                <div className="mt-3">
                  <p className="text-xs text-zinc-500 mb-2">Preview:</p>
                  <img 
                    src={formData.picture_url} 
                    alt="Preview" 
                    className="w-24 h-24 rounded-lg object-cover border-2 border-zinc-800"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=Invalid+URL';
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-yellow-500 text-black py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
              >
                <Save className="w-4 h-4 inline mr-2" />
                {editing ? 'Update' : 'Create'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setFormData({ name: '', bio: '', picture_url: '', poker_background: '' });
                  }}
                  className="px-4 bg-zinc-800 text-white py-2 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Members List */}
        <div className="space-y-4">
          {members.map(member => (
            <div key={member.member_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <img
                  src={member.picture_url}
                  alt={member.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-zinc-700"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">{member.name}</h3>
                  {member.poker_background && (
                    <p className="text-sm text-yellow-500 mb-2">{member.poker_background}</p>
                  )}
                  <p className="text-sm text-zinc-400">{member.bio}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(member)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(member.member_id)}
                    className="p-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500">
              No panel members yet. Add your first instructor above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Stream Management Section - Enhanced
function StreamManagement() {
  const [streamInfo, setStreamInfo] = useState({
    stream_title: '',
    stream_description: '',
    offline_thumbnail_url: ''
  });
  const [vdoConfig, setVdoConfig] = useState({
    room_name: '',
    vdo_embed_url: '',
    vdo_view_url: '',
    guest_invite_url: ''
  });
  const [isLive, setIsLive] = useState(false);
  const [copied, setCopied] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingVdo, setSavingVdo] = useState(false);

  const publicLivePageUrl = `${window.location.origin}/live`;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/livestream/settings`);
      if (res.ok) {
        const data = await res.json();
        setStreamInfo({
          stream_title: data.stream_title || '',
          stream_description: data.stream_description || '',
          offline_thumbnail_url: data.offline_thumbnail_url || ''
        });
        setVdoConfig({
          room_name: data.room_name || '',
          vdo_embed_url: data.vdo_embed_url || '',
          vdo_view_url: data.vdo_view_url || '',
          guest_invite_url: data.guest_invite_url || ''
        });
        setIsLive(data.is_live || false);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveStreamInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/livestream/stream-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(streamInfo)
      });
      if (res.ok) {
        alert('Stream information saved!');
      }
    } catch (error) {
      console.error('Error saving stream info:', error);
      alert('Failed to save stream information');
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveVdoConfig = async (e) => {
    e.preventDefault();
    setSavingVdo(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/livestream/vdo-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(vdoConfig)
      });
      if (res.ok) {
        alert('VDO.Ninja configuration saved!');
      }
    } catch (error) {
      console.error('Error saving VDO config:', error);
      alert('Failed to save VDO.Ninja configuration');
    } finally {
      setSavingVdo(false);
    }
  };

  const handleToggleLive = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/livestream/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_live: !isLive })
      });
      if (res.ok) {
        setIsLive(!isLive);
      }
    } catch (error) {
      console.error('Error toggling live status:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) {
      alert('No value to copy! Please save your settings first.');
      return;
    }
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const getEmbedCode = () => {
    if (!vdoConfig.vdo_embed_url) return '';
    return `<iframe src="${vdoConfig.vdo_embed_url}" width="100%" height="600" frameborder="0" allow="camera; microphone; fullscreen"></iframe>`;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Live Stream Manager</h1>
          <p className="text-zinc-400">Configure your VDO.Ninja live stream</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-zinc-500">Stream Status:</span>
          <button
            onClick={handleToggleLive}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              isLive 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'
            }`}
          >
            {isLive ? '● GO LIVE' : 'GO LIVE'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          {/* Stream Information */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
              <Video className="w-5 h-5 text-yellow-500" />
              Stream Information
            </h2>
            <p className="text-xs text-zinc-500 mb-4">Basic details shown to viewers</p>
            
            <form onSubmit={handleSaveStreamInfo} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Stream Title</label>
                <input
                  type="text"
                  value={streamInfo.stream_title}
                  onChange={(e) => setStreamInfo({ ...streamInfo, stream_title: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="SMSC Live"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Stream Description</label>
                <textarea
                  value={streamInfo.stream_description}
                  onChange={(e) => setStreamInfo({ ...streamInfo, stream_description: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm min-h-[100px]"
                  placeholder="Watch San Marcos Social Club in poker action!"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Offline Thumbnail URL (optional)</label>
                <input
                  type="url"
                  value={streamInfo.offline_thumbnail_url}
                  onChange={(e) => setStreamInfo({ ...streamInfo, offline_thumbnail_url: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="https://..."
                />
              </div>

              <button
                type="submit"
                disabled={savingInfo}
                className="w-full bg-yellow-500 text-black py-2.5 rounded-lg font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {savingInfo ? 'Saving...' : 'Save Stream Info'}
              </button>
            </form>
          </div>

          {/* VDO.Ninja URLs */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
              <Radio className="w-5 h-5 text-yellow-500" />
              VDO.Ninja URLs
            </h2>
            <p className="text-xs text-zinc-500 mb-4">Configure your VDO.Ninja room links</p>
            
            <form onSubmit={handleSaveVdoConfig} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Room Name (optional)</label>
                <input
                  type="text"
                  value={vdoConfig.room_name}
                  onChange={(e) => setVdoConfig({ ...vdoConfig, room_name: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="SMSC_Test_M2_Password_Test"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">VDO.Ninja Embed URL *</label>
                <input
                  type="url"
                  value={vdoConfig.vdo_embed_url}
                  onChange={(e) => setVdoConfig({ ...vdoConfig, vdo_embed_url: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="https://vdo.ninja/?view=room-SMSC_Test_M2_Password_Test"
                />
                <p className="text-xs text-zinc-500 mt-1">This URL shows the stream on your live page</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">VDO.Ninja View URL (for OBS)</label>
                <input
                  type="url"
                  value={vdoConfig.vdo_view_url}
                  onChange={(e) => setVdoConfig({ ...vdoConfig, vdo_view_url: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="https://vdo.ninja/?view=agEm8cnb"
                />
                <p className="text-xs text-zinc-500 mt-1">Use this URL in OBS as a browser source</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Guest Invite URL</label>
                <input
                  type="url"
                  value={vdoConfig.guest_invite_url}
                  onChange={(e) => setVdoConfig({ ...vdoConfig, guest_invite_url: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="https://vdo.ninja/?room=SMSC_Test_M2_Password_Test"
                />
                <p className="text-xs text-zinc-500 mt-1">Share with guests to let them join your stream</p>
              </div>

              <button
                type="submit"
                disabled={savingVdo}
                className="w-full bg-yellow-500 text-black py-2.5 rounded-lg font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {savingVdo ? 'Saving...' : 'Save VDO.Ninja Config'}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* Quick Copy Links */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Copy className="w-5 h-5 text-yellow-500" />
              Quick Copy Links
            </h2>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">📺 Public Live Page</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={publicLivePageUrl}
                    readOnly
                    className="flex-1 bg-black border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-300 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(publicLivePageUrl, 'public')}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                    title="Copy Public Live Page URL"
                  >
                    {copied === 'public' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">🎤 Guest Invite Link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={vdoConfig.guest_invite_url || 'Save VDO config first'}
                    readOnly
                    className="flex-1 bg-black border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-300 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(vdoConfig.guest_invite_url, 'guest')}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                    title="Copy Guest Invite URL"
                  >
                    {copied === 'guest' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">📋 Embed Code</label>
                <div className="flex items-center gap-2">
                  <textarea
                    value={getEmbedCode() || 'Save VDO embed URL first'}
                    readOnly
                    className="flex-1 bg-black border border-zinc-800 rounded px-3 py-2 text-xs text-zinc-300 font-mono min-h-[60px]"
                  />
                  <button
                    onClick={() => copyToClipboard(getEmbedCode(), 'embed')}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors self-start"
                    title="Copy Embed Code"
                  >
                    {copied === 'embed' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-yellow-500" />
              Preview
            </h2>
            
            <div className="aspect-video bg-black rounded-lg border border-zinc-700 flex items-center justify-center">
              {vdoConfig.vdo_embed_url && isLive ? (
                <iframe
                  src={vdoConfig.vdo_embed_url}
                  className="w-full h-full rounded-lg"
                  allow="camera; microphone; fullscreen; display-capture"
                  allowFullScreen
                />
              ) : (
                <div className="text-center p-8">
                  <Radio className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm font-semibold">Stream Offline</p>
                  <p className="text-zinc-600 text-xs mt-1">
                    {!vdoConfig.vdo_embed_url 
                      ? 'Add VDO embed URL and go live to preview' 
                      : 'Click "GO LIVE" to start streaming'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recommended Workflow */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-yellow-500" />
              Recommended Workflow
            </h2>
            
            <ol className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xs font-bold">1</span>
                <span><strong>Go to vdo.ninja</strong> and create a room or get your stream links</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xs font-bold">2</span>
                <span>Copy your <strong>VDO.Ninja URLs</strong> into the form above and save</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xs font-bold">3</span>
                <span>Share the <strong>Guest Invite URL</strong> with remote guests</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xs font-bold">4</span>
                <span>Use the <strong>OBS View URL</strong> in OBS as a browser source</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xs font-bold">5</span>
                <span>When ready, click <strong>GO LIVE</strong> to activate the stream on your public page</span>
              </li>
            </ol>

            <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-400">
                💡 <strong>Pro Tip:</strong> Test your stream in Preview mode before going live to ensure everything works!
              </p>
            </div>
          </div>

          {/* EXPANDED HELP SECTIONS */}
          
          {/* How This Works */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              How This Works
            </h2>
            <div className="space-y-3 text-sm text-zinc-300">
              <p><strong className="text-white">Public Live Page:</strong> This is where your viewers watch the stream at <code className="text-yellow-500 bg-black px-2 py-0.5 rounded">/live</code></p>
              <p><strong className="text-white">Guest Invite URL:</strong> Send this link to remote guests (players, co-hosts) so they can join with their camera and microphone</p>
              <p><strong className="text-white">OBS View URL:</strong> Add this in OBS as a Browser Source to bring guest feeds into your production</p>
              <p><strong className="text-white">Embed URL:</strong> Powers the live player on your website - this is what viewers see</p>
              <p className="text-xs text-zinc-500 italic mt-2">VDO.Ninja handles the remote video transport. OBS handles scene switching, overlays, and final production.</p>
            </div>
          </div>

          {/* OBS Setup Instructions */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Video className="w-5 h-5 text-red-500" />
              OBS Studio Setup
            </h2>
            <div className="space-y-3 text-sm text-zinc-300">
              <p className="text-white font-semibold mb-2">Adding Remote Guests to OBS:</p>
              <ol className="space-y-2 ml-4 list-decimal list-outside">
                <li>In OBS, create a new <strong>Browser Source</strong></li>
                <li>Paste the <strong>OBS View URL</strong> from above</li>
                <li>Set width to <code className="text-yellow-500">1920</code> and height to <code className="text-yellow-500">1080</code></li>
                <li>Check "Shutdown source when not visible" for performance</li>
                <li>Click OK - you'll now see your guests in OBS!</li>
              </ol>
              <div className="mt-3 p-3 bg-orange-500/5 border border-orange-500/20 rounded">
                <p className="text-xs text-orange-400"><strong>⚠️ Common Issues:</strong></p>
                <ul className="mt-2 space-y-1 text-xs text-orange-300 ml-4 list-disc">
                  <li><strong>Doubled audio?</strong> Mute either the Browser Source in OBS OR the guest's audio output - never have both active</li>
                  <li><strong>Feed not showing?</strong> Right-click Browser Source → Interact → check if VDO.Ninja loaded correctly</li>
                  <li><strong>Laggy video?</strong> Lower the browser source FPS to 30 in properties</li>
                </ul>
              </div>
              <p className="text-white font-semibold mt-4 mb-2">Before Going Live:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li>Test all microphone sources (yours + guests)</li>
                <li>Check camera framing and lighting</li>
                <li>Verify scene transitions work smoothly</li>
                <li>Test your internet upload speed (minimum 5 Mbps recommended)</li>
                <li>Mute desktop audio if you don't want system sounds on stream</li>
              </ul>
            </div>
          </div>

          {/* VDO.Ninja Guest Instructions */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              VDO.Ninja Guest Setup
            </h2>
            <div className="space-y-3 text-sm text-zinc-300">
              <p className="text-white font-semibold mb-2">Inviting Guests:</p>
              <ol className="space-y-2 ml-4 list-decimal">
                <li>Copy the <strong>Guest Invite URL</strong> from the Quick Copy Links section above</li>
                <li>Send it to your guest via email, text, or chat</li>
                <li>Ask them to click the link <strong>15 minutes before</strong> the stream starts</li>
              </ol>
              <p className="text-white font-semibold mt-4 mb-2">What Guests Should Do:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong>Use Google Chrome on desktop</strong> (best compatibility)</li>
                <li>Allow camera and microphone permissions when prompted</li>
                <li>Wear <strong>headphones</strong> to prevent echo/feedback</li>
                <li>Use a <strong>stable internet connection</strong> (wired Ethernet is best, strong Wi-Fi okay)</li>
                <li>Find a quiet room with good lighting</li>
                <li>Close unnecessary browser tabs and apps</li>
              </ul>
              <div className="mt-3 p-3 bg-green-500/5 border border-green-500/20 rounded">
                <p className="text-xs text-green-400"><strong>✅ Pro Tips for Guests:</strong></p>
                <ul className="mt-2 space-y-1 text-xs text-green-300 ml-4 list-disc">
                  <li>Test audio by talking - you should see your audio meter moving in VDO.Ninja</li>
                  <li>Stay on the VDO.Ninja page during the entire stream (don't navigate away)</li>
                  <li>If video freezes, refresh the page to reconnect</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Flag className="w-5 h-5 text-purple-500" />
              Troubleshooting
            </h2>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-white font-semibold mb-2">❌ No video showing on website?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Verify the <strong>Embed URL</strong> is saved correctly</li>
                  <li>Make sure you clicked <strong>GO LIVE</strong></li>
                  <li>Check the Preview section - if it shows offline, the embed URL may be wrong</li>
                  <li>Try opening the Embed URL in a new browser tab to test it directly</li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-2">❌ Guest can't join?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Double-check the <strong>Guest Invite URL</strong> was copied correctly</li>
                  <li>Ask guest to try Chrome browser instead of Safari/Firefox</li>
                  <li>Check if guest allowed camera/microphone permissions</li>
                  <li>Have guest reload the page and try again</li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-2">❌ OBS not showing guest feed?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Verify the <strong>OBS View URL</strong> is correct</li>
                  <li>Right-click the Browser Source → Properties → make sure URL is pasted</li>
                  <li>Try refreshing the Browser Source (right-click → Refresh)</li>
                  <li>Check if the browser source is visible in your current scene</li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-2">❌ Echo or doubled audio?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Check for duplicate audio: OBS browser source + guest speakers</li>
                  <li>Mute the Browser Source audio in OBS if guest is speaking through speakers</li>
                  <li>Ask guest to wear headphones (most common fix)</li>
                  <li>Lower desktop audio volume in OBS mixer</li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-2">❌ Stream offline message showing?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Confirm <strong>Stream Information</strong> and <strong>VDO.Ninja Config</strong> were both saved</li>
                  <li>Click the <strong>GO LIVE</strong> button at the top of this page</li>
                  <li>Refresh your website's live page</li>
                  <li>Check the Preview section - if it works there, it should work on your site</li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-2">❌ Changes not appearing on website?</p>
                <ul className="ml-4 list-disc text-zinc-400 space-y-1">
                  <li>Make sure you clicked <strong>Save</strong> after making changes</li>
                  <li>Refresh your website (Ctrl+R or Cmd+R)</li>
                  <li>Try a hard refresh (Ctrl+Shift+R or Cmd+Shift+R)</li>
                  <li>Clear browser cache if still not updating</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Quick Reference */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-yellow-500" />
              Quick Reference
            </h2>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-black border border-zinc-800 rounded p-3">
                  <p className="text-zinc-500 mb-1">Copy Public Live Page</p>
                  <p className="text-white font-semibold">Send to viewers</p>
                </div>
                <div className="bg-black border border-zinc-800 rounded p-3">
                  <p className="text-zinc-500 mb-1">Copy Guest Invite Link</p>
                  <p className="text-white font-semibold">Send to remote guests</p>
                </div>
                <div className="bg-black border border-zinc-800 rounded p-3">
                  <p className="text-zinc-500 mb-1">Copy Embed Code</p>
                  <p className="text-white font-semibold">Advanced use only</p>
                </div>
                <div className="bg-black border border-zinc-800 rounded p-3">
                  <p className="text-zinc-500 mb-1">OBS View URL</p>
                  <p className="text-white font-semibold">Add as Browser Source</p>
                </div>
              </div>
              <div className="p-3 bg-zinc-800 rounded">
                <p className="text-zinc-300"><strong>Remember:</strong> Save your settings, test in Preview, then GO LIVE when ready! 🎬</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Schedule Management Section
function ScheduleManagement() {
  const [schedule, setSchedule] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    scheduled_at: '', 
    duration_minutes: 60 
  });

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/schedule`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${BACKEND_URL}/api/admin/schedule/${editing}` 
        : `${BACKEND_URL}/api/admin/schedule`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        await fetchSchedule();
        setEditing(null);
        setFormData({ title: '', description: '', scheduled_at: '', duration_minutes: 60 });
      }
    } catch (error) {
      console.error('Error saving schedule item:', error);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled session?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/schedule/${scheduleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchSchedule();
      }
    } catch (error) {
      console.error('Error deleting schedule item:', error);
    }
  };

  const handleEdit = (item) => {
    setEditing(item.schedule_id);
    setFormData({
      title: item.title,
      description: item.description,
      scheduled_at: new Date(item.scheduled_at).toISOString().slice(0, 16),
      duration_minutes: item.duration_minutes
    });
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Schedule Management</h1>
          <p className="text-zinc-400">Plan and manage your livestream sessions</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editing ? 'Edit Session' : 'Add New Session'}
          </h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Session Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Monday Night Strategy Session"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[80px]"
                placeholder="What we'll cover in this session..."
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Duration (min)</label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  min="15"
                  step="15"
                  required
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-yellow-500 text-black py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editing ? 'Update' : 'Create'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setFormData({ title: '', description: '', scheduled_at: '', duration_minutes: 60 });
                  }}
                  className="px-4 bg-zinc-800 text-white py-2 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Schedule List */}
        <div className="space-y-4">
          {schedule.map(item => (
            <div key={item.schedule_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-sm text-zinc-400 mb-2">{item.description}</p>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(item.scheduled_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(item.scheduled_at).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </div>
                    <span>{item.duration_minutes} min</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.schedule_id)}
                    className="p-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {schedule.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500">
              No scheduled sessions yet. Create your first session above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Archives Management Section
function ArchivesManagement() {
  const [archives, setArchives] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    video_url: '', 
    thumbnail_url: '',
    recorded_at: '',
    duration: '1:30:00'
  });

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/archives`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setArchives(data);
      }
    } catch (error) {
      console.error('Error fetching archives:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${BACKEND_URL}/api/admin/archives/${editing}` 
        : `${BACKEND_URL}/api/admin/archives`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        await fetchArchives();
        setEditing(null);
        setFormData({ title: '', description: '', video_url: '', thumbnail_url: '', recorded_at: '', duration: '1:30:00' });
      }
    } catch (error) {
      console.error('Error saving archive:', error);
    }
  };

  const handleDelete = async (archiveId) => {
    if (!window.confirm('Are you sure you want to delete this archive?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/archives/${archiveId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchArchives();
      }
    } catch (error) {
      console.error('Error deleting archive:', error);
    }
  };

  const handleEdit = (archive) => {
    setEditing(archive.archive_id);
    setFormData({
      title: archive.title,
      description: archive.description,
      video_url: archive.video_url,
      thumbnail_url: archive.thumbnail_url,
      recorded_at: new Date(archive.recorded_at).toISOString().slice(0, 10),
      duration: archive.duration
    });
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Stream Archives</h1>
          <p className="text-zinc-400">Manage your recorded sessions</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editing ? 'Edit Archive' : 'Add New Archive'}
          </h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Video Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[80px]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Video URL</label>
              <input
                type="url"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="https://youtube.com/watch?v=..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Thumbnail URL</label>
              <input
                type="url"
                value={formData.thumbnail_url}
                onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Recorded Date</label>
                <input
                  type="date"
                  value={formData.recorded_at}
                  onChange={(e) => setFormData({ ...formData, recorded_at: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Duration</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="1:30:00"
                  required
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-yellow-500 text-black py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editing ? 'Update' : 'Create'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setFormData({ title: '', description: '', video_url: '', thumbnail_url: '', recorded_at: '', duration: '1:30:00' });
                  }}
                  className="px-4 bg-zinc-800 text-white py-2 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Archives List */}
        <div className="space-y-4">
          {archives.map(archive => (
            <div key={archive.archive_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex gap-4">
                {archive.thumbnail_url && (
                  <img
                    src={archive.thumbnail_url}
                    alt={archive.title}
                    className="w-24 h-16 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{archive.title}</h3>
                  <p className="text-sm text-zinc-400 mb-2 line-clamp-2">{archive.description}</p>
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span>{new Date(archive.recorded_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{archive.duration}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(archive)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(archive.archive_id)}
                    className="p-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {archives.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500">
              No archived sessions yet. Add your first recording above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Lessons Management Section
function LessonsManagement() {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    content: '', 
    difficulty: 'Beginner' 
  });

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/lessons`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLessons(data);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${BACKEND_URL}/api/admin/lessons/${editing}` 
        : `${BACKEND_URL}/api/admin/lessons`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        await fetchLessons();
        setEditing(null);
        setFormData({ title: '', description: '', content: '', difficulty: 'Beginner' });
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
    }
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lessons/${lessonId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchLessons();
      }
    } catch (error) {
      console.error('Error deleting lesson:', error);
    }
  };

  const handleEdit = (lesson) => {
    setEditing(lesson.lesson_id);
    setFormData({
      title: lesson.title,
      description: lesson.description,
      content: lesson.content,
      difficulty: lesson.difficulty
    });
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Lessons Management</h1>
          <p className="text-zinc-400">Create and manage poker strategy lessons</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editing ? 'Edit Lesson' : 'Add New Lesson'}
          </h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Lesson Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Understanding Position Play"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Difficulty Level</label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Short Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[60px]"
                placeholder="Brief overview..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Lesson Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[200px]"
                placeholder="Full lesson content..."
                required
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-yellow-500 text-black py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editing ? 'Update' : 'Create'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setFormData({ title: '', description: '', content: '', difficulty: 'Beginner' });
                  }}
                  className="px-4 bg-zinc-800 text-white py-2 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Lessons List */}
        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {lessons.map(lesson => (
            <div key={lesson.lesson_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      lesson.difficulty === 'Beginner' ? 'bg-blue-500/10 text-blue-400' :
                      lesson.difficulty === 'Intermediate' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-green-500/10 text-green-400'
                    }`}>
                      {lesson.difficulty}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{lesson.title}</h3>
                  <p className="text-sm text-zinc-400">{lesson.description}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(lesson)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(lesson.lesson_id)}
                    className="p-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {lessons.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500">
              No lessons yet. Create your first lesson above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Forum Management Section (Simple moderation view)
function ForumManagement() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/forum/threads`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // DEFENSIVE: Ensure data is always an array
        setThreads(Array.isArray(data) ? data : []);
      } else {
        // API returned error status - set empty array
        setThreads([]);
        if (res.status !== 404) {
          setError('Failed to load forum threads. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
      // DEFENSIVE: On error, set empty array to prevent crash
      setThreads([]);
      setError('Unable to connect to forum service.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this thread?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/threads/${threadId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchThreads();
      }
    } catch (error) {
      console.error('Error deleting thread:', error);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Forum Management</h1>
        <p className="text-zinc-400">Moderate community discussions</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A227] mb-4"></div>
          <p className="text-zinc-400">Loading forum threads...</p>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="bg-red-900/20 border border-red-900/50 rounded-xl p-6 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <h3 className="font-semibold text-red-400">Error Loading Forum</h3>
          </div>
          <p className="text-red-300/80 text-sm mb-4">{error}</p>
          <button
            onClick={fetchThreads}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Threads List */}
      {!loading && !error && (
        <div className="space-y-4">
          {threads.map(thread => (
            <div key={thread.thread_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">{thread.title}</h3>
                  <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{thread.content}</p>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span>by {thread.author_name || 'Unknown'}</span>
                    <span>•</span>
                    <span>{thread.reply_count || 0} replies</span>
                    <span>•</span>
                    <span>{thread.created_at ? new Date(thread.created_at).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`/community/thread/${thread.thread_id}`, '_blank')}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                    data-testid={`view-thread-${thread.thread_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteThread(thread.thread_id)}
                    className="p-2 bg-red-900/50 hover:bg-red-900 text-red-400 rounded-lg transition-colors"
                    data-testid={`delete-thread-${thread.thread_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Empty State */}
          {threads.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
              <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Forum Threads Yet</h3>
              <p className="text-zinc-400 mb-6">
                Threads and replies will appear here once members begin posting.
              </p>
              <button
                onClick={fetchThreads}
                className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                data-testid="refresh-forum-button"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Main Admin Router Component
// Settings Management Section - NEW
function SettingsManagement() {
  const [trialDuration, setTrialDuration] = useState(3);
  const [saving, setSaving] = useState(false);
  const [trialUsers, setTrialUsers] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchTrialUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trial-settings`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTrialDuration(data.free_trial_duration);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchTrialUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trial-users`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTrialUsers(data.trial_users);
      }
    } catch (error) {
      console.error('Error fetching trial users:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trial-settings?free_trial_duration=${trialDuration}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="p-8">
      <h1 className="text-4xl font-bold text-white mb-2">Settings</h1>
      <p className="text-zinc-400 mb-8">Manage platform settings and free trial configuration</p>

      {/* Free Trial Settings */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-yellow-500" />
          Free Trial Configuration
        </h2>
        
        <div className="max-w-md">
          <label className="block text-sm font-medium text-zinc-300 mb-2">
            Free Trial Duration (Days)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="1"
              max="30"
              value={trialDuration}
              onChange={(e) => setTrialDuration(parseInt(e.target.value) || 1)}
              className="flex-1 bg-black border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            This determines how many days new trial users get free access. Default: 3 days.
          </p>
        </div>
      </div>

      {/* Trial Users List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-500" />
            Trial Users
          </h2>
          <button
            onClick={fetchTrialUsers}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            Refresh
          </button>
        </div>
        
        {trialUsers.length === 0 ? (
          <p className="text-zinc-500">No trial users yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-3 px-4 text-zinc-400 font-semibold text-sm">Email</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-semibold text-sm">Name</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-semibold text-sm">Status</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-semibold text-sm">Trial Ends</th>
                  <th className="text-left py-3 px-4 text-zinc-400 font-semibold text-sm">Created</th>
                </tr>
              </thead>
              <tbody>
                {trialUsers.map((user) => (
                  <tr key={user.user_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                    <td className="py-3 px-4 text-white text-sm">{user.email}</td>
                    <td className="py-3 px-4 text-white text-sm">{user.name}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        user.trial_status === 'active' 
                          ? 'bg-green-500/10 text-green-400' 
                          : 'bg-red-500/10 text-red-400'
                      }`}>
                        {user.trial_status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-zinc-400 text-sm">{formatDate(user.trial_end_date)}</td>
                    <td className="py-3 px-4 text-zinc-400 text-sm">{formatDate(user.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}




// Contact Us Management Section
function ContactUsManagement() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contacts`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        await fetchContacts();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact submission?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await fetchContacts();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Loading contacts...</div>;
  }

  const newContacts = contacts.filter(c => c.status === 'new');
  const respondedContacts = contacts.filter(c => c.status === 'responded');
  const resolvedContacts = contacts.filter(c => c.status === 'resolved');

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-blue-500" />
          Contact Submissions
        </h1>
        <button
          onClick={fetchContacts}
          className="text-blue-400 hover:text-blue-300 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
          <div className="text-3xl font-bold text-yellow-500 mb-2">{newContacts.length}</div>
          <div className="text-sm text-yellow-500/80">New Messages</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
          <div className="text-3xl font-bold text-blue-500 mb-2">{respondedContacts.length}</div>
          <div className="text-sm text-blue-500/80">Responded</div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
          <div className="text-3xl font-bold text-green-500 mb-2">{resolvedContacts.length}</div>
          <div className="text-sm text-green-500/80">Resolved</div>
        </div>
      </div>

      {/* Contact List */}
      <div className="space-y-4">
        {contacts.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No contact submissions yet</p>
          </div>
        ) : (
          contacts.map((contact) => (
            <div 
              key={contact.contact_id} 
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{contact.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      contact.status === 'new' ? 'bg-yellow-500/10 text-yellow-400' :
                      contact.status === 'responded' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-green-500/10 text-green-400'
                    }`}>
                      {contact.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {contact.email}
                    </span>
                    {contact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {contact.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(contact.created_at)}
                    </span>
                  </div>
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-zinc-800 rounded-lg text-sm text-zinc-300">
                      Subject: {contact.subject}
                    </span>
                  </div>
                  <p className="text-zinc-300 bg-black/30 rounded-lg p-4 whitespace-pre-wrap">
                    {contact.message}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 pt-4 border-t border-zinc-800">
                <select
                  value={contact.status}
                  onChange={(e) => updateStatus(contact.contact_id, e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="new">New</option>
                  <option value="responded">Responded</option>
                  <option value="resolved">Resolved</option>
                </select>
                
                <a 
                  href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  Reply via Email
                </a>
                
                <button
                  onClick={() => deleteContact(contact.contact_id)}
                  className="ml-auto px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const location = useLocation();
  
  // Determine active section from pathname
  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return 'dashboard';
    if (path.includes('/stream')) return 'stream';
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/panel')) return 'panel';
    if (path.includes('/lessons')) return 'lessons';
    if (path.includes('/archives')) return 'archives';
    if (path.includes('/forum')) return 'forum';
    if (path.includes('/contacts')) return 'contacts';
    if (path.includes('/settings')) return 'settings';
    return 'dashboard';
  };

  const activeSection = getActiveSection();

  // Render different sections based on route
  const renderSection = () => {
    switch (activeSection) {
      case 'stream':
        return <StreamManagement />;
      case 'panel':
        return <PanelManagement />;
      case 'schedule':
        return <ScheduleManagement />;
      case 'archives':
        return <ArchivesManagement />;
      case 'lessons':
        return <LessonsManagement />;
      case 'forum':
        return <ForumManagement />;
      case 'contacts':
        return <ContactUsManagement />;
      case 'settings':
        return <SettingsManagement />;
      case 'dashboard':
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <ProtectedRoute requireAdmin>
      <AdminLayout activeSection={activeSection}>
        {renderSection()}
      </AdminLayout>
    </ProtectedRoute>
  );
}
